<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<script>
import { Orientation } from '@src/store/dicom-web/classification-typing';
import { scaleLinear } from 'd3-scale';
import {
  interpolateCividis,
  interpolateInferno,
  interpolateMagma,
  interpolatePlasma,
  interpolateRdBu,
  interpolateRdYlBu,
  interpolateTurbo,
  interpolateViridis,
} from 'd3-scale-chromatic';
import { select } from 'd3-selection';
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';

import { useAnalysisStore } from '@/src/store/analysis';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

const colormaps = {
  'plasma': interpolatePlasma,
  'viridis': interpolateViridis,
  'turbo': interpolateTurbo,
  'magma': interpolateMagma,
  'inferno': interpolateInferno,
  'cividis': interpolateCividis,
  'red-yellow-blue': interpolateRdYlBu,
  'red-blue': interpolateRdBu,
};

export default {
  name: 'SimplePolarmap',
  props: {
    insertionPoint: {
      type: Number,
      default: 0,
    },
    colormap: {
      type: String,
      default: 'plasma',
      validator: v => Object.keys(colormaps).includes(v),
    },
    interpolationMode: {
      type: String,
      default: 'circumferential',
      validator: v => ['none', 'circumferential'].includes(v),
    },
    innerCircleRadius: { type: Number, default: 0.6 },
    pixelSize: { type: Number, default: 1 },
    title: { type: String, default: '' },
    unit: { type: String, default: '' },
    trueMorphology: { type: Boolean, default: false },
    morphology: {
      type: Array,
      default: () => null,
      validator: v => v === null || v.length === 60,
    },
    morphologyScale: { type: Number, default: 0.08 },
    biomarkerTarget: { type: String, default: 'ocgDiaMaxIntensity' },
    viewerID: { type: String, default: null },
    colormapMode: {
      type: String,
      default: 'raw',
      validator: v => ['raw', 'z_score'].includes(v),
    },
    muHealthy: { type: Number, default: 1.15629 },
    sigmaHealthy: { type: Number, default: 0.173803 },
    clipN: { type: Number, default: 2 },
    zDiff: { type: Number, default: -0.45 },
    badColor: { type: String, default: '#DA3459' },
    goodColor: { type: String, default: '#2da0ffbe' },
    showTerritories: { type: Boolean, default: true },
    clinicalLabel: { type: String, default: '' },
    /** Display orientation: SAX keeps circular map, LAX renders a rotated U-annulus */
    orientation: {
      type: Orientation,
      default: Orientation.Sax,
      validator: v => [Orientation.Sax, Orientation.Lax2CV].includes(v),
    },
    /** Rotation angle (degrees) for LAX U-shape; positive rotates clockwise on screen */
    laxRotationDeg: {
      type: Number,
      default: 55,
    },
  },
  setup(props) {
    const vm = getCurrentInstance();
    const gradientId = `colorbar-gradient-${vm?.uid ?? 'fallback'}`;

    const analysisStore = useAnalysisStore();
    const biomarkerContainer = computed(() =>
      analysisStore.getBiomarkerContainer(),
    );
    const viewerIndx = computed(() => {
      const viewportStateStore = useViewportStateStore();
      return viewportStateStore.getSelectedImageIndices(props.viewerID);
    });

    const getDataArray = () => {
      const { sliceIdx, seriesIdx } = viewerIndx.value;
      const currentStudyStore = useCurrentStudyStore();

      const seriesUID = currentStudyStore.getSeriesUID(seriesIdx);
      const biomarkerData
        = biomarkerContainer.value.getAHADataBySliceIndexAndType(
          seriesUID,
          sliceIdx,
          props.biomarkerTarget,
        );

      if (!biomarkerData || !biomarkerData[0]) {
        return null;
      }

      const biomarkerDataArray = biomarkerData[0]?.getData();
      return biomarkerDataArray && biomarkerDataArray.length === 60
        ? biomarkerDataArray
        : null;
    };

    const canvas = ref(null);
    // const paddingX = ref(60);
    // const paddingY = ref(10);
    const tooltip = ref({
      visible: false,
      x: 0,
      y: 0,
      label: '',
    });

    const geometry = ref({
      cx: 0,
      cy: 0,
      innerR: 0,
      outerR: 0,
    });

    let ctx;
    let resizeObserver;
    let lastObservedSize = '';

    const stats = computed(() => {
      const values = getDataArray();
      if (!values || !values.length) return { mean: 0, stdDev: 0 };
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const variance
        = values.reduce((acc, val) => acc + (val - mean) ** 2, 0) / values.length;
      return { mean, stdDev: Math.sqrt(variance) };
    });

    const minValue = computed(() => {
      if (props.colormapMode === 'z_score') {
        return -props.clipN;
      }

      const range = biomarkerContainer.value.getBiomarkerNormalRange(
        props.biomarkerTarget,
      );

      if (range) {
        return range[0];
      }
      return stats.value.mean - 2 * stats.value.stdDev;
    });

    const maxValue = computed(() => {
      if (props.colormapMode === 'z_score') {
        return props.clipN;
      }
      const range = biomarkerContainer.value.getBiomarkerNormalRange(
        props.biomarkerTarget,
      );
      if (range) {
        return range[1];
      }
      return stats.value.mean + 2 * stats.value.stdDev;
    });

    /**
     * Calculate z-score from raw value
     */
    const calculateZScore = (value) => {
      if (value == null) return null;
      const z = (value - props.muHealthy) / Math.max(props.sigmaHealthy, 1e-12);
      return Math.max(-props.clipN, Math.min(props.clipN, z));
    };

    /**
     * Create a linear interpolation between two colors
     */
    const interpolateColor = (color1, color2, t) => {
      // Parse hex colors
      const c1 = color1.replace(/^#/, '');
      const c2 = color2.replace(/^#/, '');

      const r1 = parseInt(c1.substring(0, 2), 16);
      const g1 = parseInt(c1.substring(2, 4), 16);
      const b1 = parseInt(c1.substring(4, 6), 16);
      const a1 = c1.length > 6 ? parseInt(c1.substring(6, 8), 16) / 255 : 1;

      const r2 = parseInt(c2.substring(0, 2), 16);
      const g2 = parseInt(c2.substring(2, 4), 16);
      const b2 = parseInt(c2.substring(4, 6), 16);
      const a2 = c2.length > 6 ? parseInt(c2.substring(6, 8), 16) / 255 : 1;

      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      const a = a1 + (a2 - a1) * t;

      return `rgba(${r},${g},${b},${a.toFixed(2)})`;
    };

    /**
     * Get z-score color with TwoSlopeNorm-like mapping
     */
    const getZScoreColor = (zValue) => {
      if (zValue == null) return 'rgba(0,0,0,0)';

      // Normalize to [0, 1] around z_diff
      let t;
      if (zValue < props.zDiff) {
        // Map from -clipN to zDiff to [0, 0.5]
        t = 0.5 * ((zValue + props.clipN) / (props.zDiff + props.clipN));
      }
      else {
        // Map from zDiff to clipN to [0.5, 1]
        t = 0.5 + 0.5 * ((zValue - props.zDiff) / (props.clipN - props.zDiff));
      }

      t = Math.max(0, Math.min(1, t));

      // Interpolate from bad_color (left) to good_color (right)
      return interpolateColor(props.badColor, props.goodColor, t);
    };

    // Normalized morphology: maps raw morphology values to [-1, 1] centered on mean
    const normalizedMorphology = computed(() => {
      const morph = props.trueMorphology
        ? props.morphology || defaultMorphology
        : null;
      if (!morph) return null;

      const min = Math.min(...morph);
      const max = Math.max(...morph);
      const range = max - min;
      if (range === 0) return morph.map(() => 0);

      // Map to [-1, 1]: 0 = mean thickness, +1 = max, -1 = min
      return morph.map(v => ((v - min) / range) * 2 - 1);
    });

    /**
     * Get the deformed outer radius at a given angle.
     * Uses circumferential interpolation on the morphology data for a smooth contour.
     */
    const getOuterRAtAngle = (theta, outerR) => {
      const nm = normalizedMorphology.value;
      if (!nm) return outerR;

      const S = 60;
      const segWidth = (2 * Math.PI) / S;

      // Offset by half segment for centering (same as data interpolation)
      const thetaAdj = theta - segWidth / 2;
      const thetaNorm
        = ((thetaAdj % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      const segF = (thetaNorm / (2 * Math.PI)) * S;
      const si = Math.floor(segF) % S;
      const t = segF - Math.floor(segF);
      const si1 = (si + 1) % S;

      // Interpolated morphology value at this angle
      const morphVal = nm[si] + (nm[si1] - nm[si]) * t;

      return outerR + morphVal * props.morphologyScale * outerR;
    };

    /**
     * Convert pixel coords to the angle (theta) used for sector mapping.
     * Returns null if the point is outside the ring.
     */
    const pixelToTheta = (x, y, cx, cy) => {
      const dx = x - cx;
      const dy = y - cy;
      const screenAngle = Math.atan2(dx, -dy);
      const insertionRad = (props.insertionPoint * Math.PI) / 180;
      return (
        (((insertionRad - screenAngle) % (2 * Math.PI)) + 2 * Math.PI)
        % (2 * Math.PI)
      );
    };

    const pixelToRadius = (x, y, cx, cy) => {
      const dx = x - cx;
      const dy = y - cy;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const normalizeAngle = theta =>
      ((theta % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    const dataThetaToScreenAngle = theta =>
      (props.insertionPoint * Math.PI) / 180 - theta - Math.PI / 2;

    const getLaxRotationRad = () => (props.laxRotationDeg * Math.PI) / 180;

    const rotatePoint = (x, y, cx, cy, angleRad) => {
      const c = Math.cos(angleRad);
      const s = Math.sin(angleRad);
      const dx = x - cx;
      const dy = y - cy;
      return {
        x: cx + dx * c - dy * s,
        y: cy + dx * s + dy * c,
      };
    };

    const getUPointsAtT = (cx, cy, innerR, outerR, t, rotationRad = 0) => {
      const Ro = outerR * 0.8;
      const Ri = innerR * 0.8;
      const H = outerR * 0.8;
      const cyPrime = cy + outerR * 0.2;
      const Rm = (Ro + Ri) / 2;
      const lStraight = H;
      const LArc = Math.PI * Rm;
      const LTotal = 2 * H + LArc;
      const u = t * LTotal;

      let localRo = Ro;
      if (props.trueMorphology && normalizedMorphology.value) {
        const nm = normalizedMorphology.value;
        const S = 60;
        const segF = t * (S - 1);
        const si = Math.max(0, Math.min(S - 1, Math.floor(segF)));
        const frac = segF - si;
        const si1 = Math.min(S - 1, si + 1);
        const morphVal = nm[si] + (nm[si1] - nm[si]) * frac;
        localRo = Ro + morphVal * props.morphologyScale * Ro;
      }

      let result;

      if (u <= lStraight) {
        const yp = -H + u;
        result = {
          outer: { x: cx - localRo, y: cyPrime + yp },
          inner: { x: cx - Ri, y: cyPrime + yp },
          label: { x: cx - localRo - 18, y: cyPrime + yp },
        };
      }
      else if (u <= lStraight + LArc) {
        const alpha = (u - lStraight) / Rm;
        const theta = Math.PI - alpha;
        result = {
          outer: {
            x: cx + localRo * Math.cos(theta),
            y: cyPrime + localRo * Math.sin(theta),
          },
          inner: {
            x: cx + Ri * Math.cos(theta),
            y: cyPrime + Ri * Math.sin(theta),
          },
          label: {
            x: cx + (localRo + 18) * Math.cos(theta),
            y: cyPrime + (localRo + 18) * Math.sin(theta),
          },
        };
      }
      else {
        const yp = -(u - (lStraight + LArc));
        result = {
          outer: { x: cx + localRo, y: cyPrime + yp },
          inner: { x: cx + Ri, y: cyPrime + yp },
          label: { x: cx + localRo + 18, y: cyPrime + yp },
        };
      }

      if (rotationRad === 0) return result;
      return {
        outer: rotatePoint(result.outer.x, result.outer.y, cx, cy, rotationRad),
        inner: rotatePoint(result.inner.x, result.inner.y, cx, cy, rotationRad),
        label: rotatePoint(result.label.x, result.label.y, cx, cy, rotationRad),
      };
    };

    const getUPathInfo = (x, y, cx, cy, innerR, outerR, rotationRad = 0) => {
      const Ro = outerR * 0.8;
      const Ri = innerR * 0.8;
      const H = outerR * 0.8;
      const cyprime = cy + outerR * 0.2;
      const Rm = (Ro + Ri) / 2;
      const lStraight = H;
      const LArc = Math.PI * Rm;
      const LTotal = 2 * H + LArc;

      const point
        = rotationRad === 0 ? { x, y } : rotatePoint(x, y, cx, cy, -rotationRad);

      const xp = point.x - cx;
      const yp = point.y - cyprime;

      let u = 0;
      let rho = 0;

      if (yp < 0) {
        if (xp < 0) {
          // Left side
          rho = -xp;
          if (yp < -H) return null;
          u = yp + H;
        }
        else {
          // Right side
          rho = xp;
          if (yp < -H) return null;
          u = lStraight + LArc + -yp;
        }
      }
      else {
        // Bottom arc
        rho = Math.sqrt(xp * xp + yp * yp);
        const alpha = Math.PI - Math.atan2(yp, xp);
        u = lStraight + alpha * Rm;
      }

      let localRo = Ro;
      if (props.trueMorphology && normalizedMorphology.value) {
        const nm = normalizedMorphology.value;
        const S = 60;
        const tVal = u / LTotal;
        const segF = tVal * (S - 1);
        const si = Math.max(0, Math.min(S - 1, Math.floor(segF)));
        const frac = segF - si;
        const si1 = Math.min(S - 1, si + 1);
        const morphVal = nm[si] + (nm[si1] - nm[si]) * frac;
        localRo = Ro + morphVal * props.morphologyScale * Ro;
      }

      if (rho < Ri || rho > localRo) return null;
      return { t: u / LTotal };
    };

    const getLaxBounds = (cx, cy, innerR, outerR, rotationRad) => {
      const numSamples = 180;
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      for (let i = 0; i <= numSamples; i++) {
        const t = i / numSamples;
        const pts = getUPointsAtT(cx, cy, innerR, outerR, t, rotationRad);
        minX = Math.min(minX, pts.outer.x, pts.inner.x);
        maxX = Math.max(maxX, pts.outer.x, pts.inner.x);
        minY = Math.min(minY, pts.outer.y, pts.inner.y);
        maxY = Math.max(maxY, pts.outer.y, pts.inner.y);
      }

      return { minX, maxX, minY, maxY };
    };

    // LAD is centered on insertion (theta=0) and split into two equal halves.
    // The remaining map is split equally for RCA and LCx in counter-clockwise order.
    const getTerritoryLayout = () => {
      const oneThird = (2 * Math.PI) / 3;
      const ladHalf = oneThird / 2;
      const ladStart = normalizeAngle(-ladHalf);
      const ladEnd = normalizeAngle(ladHalf);
      const rcaEnd = normalizeAngle(ladEnd + oneThird);

      return {
        boundaries: [ladStart, ladEnd, rcaEnd],
        labels: [
          { name: 'LAD', theta: 0 },
          { name: 'RCA', theta: normalizeAngle(ladEnd + oneThird / 2) },
          {
            name: 'LCx',
            theta: normalizeAngle(ladEnd + oneThird + oneThird / 2),
          },
        ],
      };
    };

    const getColor = (value) => {
      if (value == null) return 'rgba(0,0,0,0)';

      if (props.colormapMode === 'z_score') {
        const zScore = calculateZScore(value);
        return getZScoreColor(zScore);
      }

      const t = (value - minValue.value) / (maxValue.value - minValue.value);
      return colormaps[props.colormap](Math.max(0, Math.min(1, t)));
    };

    const getSectorInfo = (x, y, cx, cy, innerR, outerR) => {
      const data = getDataArray();
      const S = 60;

      if (props.orientation === Orientation.Lax2CV) {
        const uInfo = getUPathInfo(
          x,
          y,
          cx,
          cy,
          innerR,
          outerR,
          getLaxRotationRad(),
        );
        if (!uInfo) return null;

        const t = Math.max(0, Math.min(1, uInfo.t));
        const si = Math.min(S - 1, Math.floor(t * S));

        if (props.interpolationMode === 'none') {
          return { index: si, value: data[si] };
        }

        const segF = t * (S - 1);
        const base = Math.floor(segF);
        const frac = segF - base;
        const baseNext = Math.min(S - 1, base + 1);
        const value = data[base] + (data[baseNext] - data[base]) * frac;

        return { index: si, value };
      }

      const r = pixelToRadius(x, y, cx, cy);
      if (r < innerR) return null;

      const theta = pixelToTheta(x, y, cx, cy);
      const localOuterR = getOuterRAtAngle(theta, outerR);
      if (r > localOuterR) return null;

      const segmentWidth = (2 * Math.PI) / S;
      const si = Math.floor((theta / (2 * Math.PI)) * S) % S;

      if (props.interpolationMode === 'none') {
        return { index: si, value: data[si] };
      }

      const thetaAdj = theta - segmentWidth / 2;
      const thetaNorm
        = ((thetaAdj % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      const segF = (thetaNorm / (2 * Math.PI)) * S;
      const base = Math.floor(segF) % S;
      const t = segF - Math.floor(segF);
      const baseNext = (base + 1) % S;
      const value = data[base] + (data[baseNext] - data[base]) * t;

      return { index: si, value };
    };

    const computeValue = (x, y, cx, cy, innerR, outerR, data) => {
      const S = 60;

      if (props.orientation === Orientation.Lax2CV) {
        const uInfo = getUPathInfo(
          x,
          y,
          cx,
          cy,
          innerR,
          outerR,
          getLaxRotationRad(),
        );
        if (!uInfo) return null;

        const t = Math.max(0, Math.min(1, uInfo.t));
        if (props.interpolationMode === 'none') {
          const si = Math.min(S - 1, Math.floor(t * S));
          return data[si];
        }

        const segF = t * (S - 1);
        const si = Math.floor(segF);
        const st = segF - si;
        const si1 = Math.min(S - 1, si + 1);
        return data[si] + (data[si1] - data[si]) * st;
      }

      const r = pixelToRadius(x, y, cx, cy);
      if (r < innerR) return null;

      const theta = pixelToTheta(x, y, cx, cy);
      const localOuterR = getOuterRAtAngle(theta, outerR);
      if (r > localOuterR) return null;

      const segmentWidth = (2 * Math.PI) / S;

      if (props.interpolationMode === 'none') {
        const si = Math.floor((theta / (2 * Math.PI)) * S) % S;
        return data[si];
      }

      // Circumferential interpolation: offset by half segment for centering
      const thetaAdj = theta - segmentWidth / 2;
      const thetaNorm
        = ((thetaAdj % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      const segF = (thetaNorm / (2 * Math.PI)) * S;
      const si = Math.floor(segF) % S;
      const st = segF - Math.floor(segF);
      const si1 = (si + 1) % S;

      return data[si] + (data[si1] - data[si]) * st;
    };

    /**
     * Build the deformed outer contour as an array of {x, y} points,
     * sampled at a fine angular resolution for smooth rendering.
     */
    const buildOuterContour = (cx, cy, outerR, numPoints = 360) => {
      const points = [];
      for (let i = 0; i < numPoints; i++) {
        const fraction = i / numPoints;
        const theta = fraction * 2 * Math.PI; // data-space angle [0, 2π]

        const localR = getOuterRAtAngle(theta, outerR);

        // Convert back to screen coordinates
        // theta=0 is 12 o'clock in data space; need to add insertion offset back for screen
        const screenAngle
          = (props.insertionPoint * Math.PI) / 180 - theta - Math.PI / 2;

        points.push({
          x: cx + localR * Math.cos(screenAngle),
          y: cy + localR * Math.sin(screenAngle),
        });
      }
      return points;
    };

    const buildLaxAnnulusPath = (cx, cy, innerR, outerR, rotationRad = 0) => {
      const numPoints = 60;
      const outerPoints = [];
      const innerPoints = [];

      for (let i = 0; i <= numPoints; i++) {
        const t = i / numPoints;
        const pts = getUPointsAtT(cx, cy, innerR, outerR, t, rotationRad);
        outerPoints.push(pts.outer);
        innerPoints.push(pts.inner);
      }

      innerPoints.reverse();

      const allPoints = [...outerPoints, ...innerPoints];
      return `M${allPoints
        .map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`)
        .join('L')}Z`;
    };

    const draw = () => {
      const c = canvas.value;
      if (!c) return;
      const parent = c.parentElement;
      c.width = parent.clientWidth;
      c.height = parent.clientHeight;
      ctx = c.getContext('2d');
      ctx.clearRect(0, 0, c.width, c.height);

      const data = getDataArray();
      if (!data) {
        // Show "No Data Available" message
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.font = '16px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('No Data Available', c.width / 2, c.height / 2);

        // Draw ring outline even with no data
        let svg = select(parent).select('svg');
        if (svg.empty()) {
          svg = select(parent)
            .append('svg')
            .style('position', 'absolute')
            .style('top', '0')
            .style('left', '0')
            .style('pointer-events', 'none');
        }
        svg.attr('width', c.width).attr('height', c.height);
        return;
      }

      const size = props.pixelSize;
      const legendWidth = 15;
      const legendLabelSpace = 38;
      const legendGap = 35;
      const marginX = 12;
      const marginY = 20;
      const availableW = Math.max(40, c.width - marginX * 2);
      const availableH = Math.max(40, c.height - marginY * 2);
      const laxRotationRad = getLaxRotationRad();

      let outerR;
      let innerR;
      let cx;
      let cy;
      let legendX;
      let laxBounds = null;

      if (props.orientation === Orientation.Lax2CV) {
        const unitOuterR = 100;
        const unitInnerR = unitOuterR * props.innerCircleRadius;
        const unitBounds = getLaxBounds(
          0,
          0,
          unitInnerR,
          unitOuterR,
          laxRotationRad,
        );
        const shapeWidthPerR
          = (unitBounds.maxX - unitBounds.minX) / unitOuterR || 1;
        const shapeHeightPerR
          = (unitBounds.maxY - unitBounds.minY) / unitOuterR || 1;

        const outerRByH = availableH / shapeHeightPerR;
        const outerRByW
          = (availableW - legendGap - legendWidth - legendLabelSpace)
            / shapeWidthPerR;
        outerR = Math.max(8, Math.min(outerRByH, outerRByW));
        innerR = outerR * props.innerCircleRadius;

        const centeredBounds = getLaxBounds(
          0,
          0,
          innerR,
          outerR,
          laxRotationRad,
        );
        const shapeWidth = centeredBounds.maxX - centeredBounds.minX;
        const shapeHeight = centeredBounds.maxY - centeredBounds.minY;
        const contentWidth
          = shapeWidth + legendGap + legendWidth + legendLabelSpace;
        const contentLeft = (c.width - contentWidth) / 2;
        const contentTop = (c.height - shapeHeight) / 2;

        cx = contentLeft - centeredBounds.minX;
        cy = contentTop - centeredBounds.minY;
        legendX = contentLeft + shapeWidth + legendGap;
        laxBounds = getLaxBounds(cx, cy, innerR, outerR, laxRotationRad);
      }
      else {
        const outerRByH = availableH / 2;
        const outerRByW
          = (availableW - legendGap - legendWidth - legendLabelSpace) / 2;
        outerR = Math.max(8, Math.min(outerRByH, outerRByW));
        innerR = outerR * props.innerCircleRadius;

        const shapeWidth = 2 * outerR;
        const contentWidth
          = shapeWidth + legendGap + legendWidth + legendLabelSpace;
        const contentLeft = (c.width - contentWidth) / 2;
        cx = contentLeft + outerR;
        cy = c.height / 2;
        legendX = contentLeft + shapeWidth + legendGap;
      }

      // Account for morphology when computing bounding box for pixel loop
      const maxOuterRBase = props.trueMorphology
        ? outerR * (1 + props.morphologyScale)
        : outerR;
      const maxOuterR
        = props.orientation === Orientation.Lax2CV && laxBounds
          ? Math.max(
            Math.abs(laxBounds.maxX - cx),
            Math.abs(laxBounds.minX - cx),
            Math.abs(laxBounds.maxY - cy),
            Math.abs(laxBounds.minY - cy),
          ) + size
          : maxOuterRBase;

      geometry.value = { cx, cy, innerR, outerR };

      // Draw polar map pixels
      for (let yy = cy - maxOuterR; yy < cy + maxOuterR; yy += size) {
        for (let xx = cx - maxOuterR; xx < cx + maxOuterR; xx += size) {
          const v = computeValue(
            xx + size / 2,
            yy + size / 2,
            cx,
            cy,
            innerR,
            outerR,
            data,
          );
          if (v !== null) {
            ctx.fillStyle = getColor(v);
            ctx.fillRect(xx, yy, size, size);
          }
        }
      }

      // SVG overlay for circles, marker, colorbar, text
      let svg = select(parent).select('svg');
      if (svg.empty()) {
        svg = select(parent)
          .append('svg')
          .style('position', 'absolute')
          .style('top', '0')
          .style('left', '0')
          .style('pointer-events', 'none');
      }
      svg.attr('width', c.width).attr('height', c.height);

      // --- Boundary + insertion point marker ---
      const shapeGroup = svg.selectAll('g.circles').data([null]);
      const shapeGroupAll = shapeGroup
        .enter()
        .append('g')
        .classed('circles', true)
        .merge(shapeGroup)
        .attr('transform', `translate(${cx},${cy})`);

      if (props.orientation === Orientation.Lax2CV) {
        shapeGroupAll.selectAll('circle.inner').remove();
        shapeGroupAll.selectAll('circle.outer').remove();
        shapeGroupAll.selectAll('path.outer-contour').remove();

        const laxPath = buildLaxAnnulusPath(
          0,
          0,
          innerR,
          outerR,
          laxRotationRad,
        );

        shapeGroupAll
          .selectAll('path.lax-boundary')
          .data([laxPath])
          .join('path')
          .classed('lax-boundary', true)
          .attr('d', d => d)
          .attr('fill', 'none')
          .attr('stroke', 'white')
          .attr('stroke-width', 1);

        const markerPts = getUPointsAtT(
          0,
          0,
          innerR,
          outerR,
          1.0,
          laxRotationRad,
        );
        shapeGroupAll
          .selectAll('circle.marker')
          .data([markerPts.outer])
          .join('circle')
          .classed('marker', true)
          .attr('cx', d => d.x)
          .attr('cy', d => d.y)
          .attr('r', 4)
          .attr('fill', 'white');

        if (props.showTerritories) {
          const labelT = [
            { name: 'LAD', t: 1 / 6 },
            { name: 'RCA', t: 1 / 2 },
            { name: 'LCx', t: 6 / 6 },
          ];

          const territoryLabelData = labelT.map((lb) => {
            const pts = getUPointsAtT(
              0,
              0,
              innerR,
              outerR,
              lb.t,
              laxRotationRad,
            );
            return { name: lb.name, x: pts.label.x, y: pts.label.y };
          });

          shapeGroupAll
            .selectAll('text.territory-label')
            .data(territoryLabelData)
            .join('text')
            .classed('territory-label', true)
            .attr('x', d => d.x)
            .attr('y', d => d.y)
            .attr('fill', 'white')
            .attr('font-size', '11px')
            .attr('font-family', 'sans-serif')
            .attr('font-weight', 'bold')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .text(d => d.name);
        }
        else {
          shapeGroupAll.selectAll('line.territory-separator').remove();
          shapeGroupAll.selectAll('text.territory-label').remove();
        }
      }
      else {
        shapeGroupAll.selectAll('path.lax-boundary').remove();

        // Inner circle (always circular)
        shapeGroupAll
          .selectAll('circle.inner')
          .data([innerR])
          .join('circle')
          .classed('inner', true)
          .attr('r', d => d)
          .attr('fill', 'none')
          .attr('stroke', 'white')
          .attr('stroke-width', 1);

        // Outer boundary: deformed path or perfect circle
        if (props.trueMorphology && normalizedMorphology.value) {
          // Remove circle if it exists
          shapeGroupAll.selectAll('circle.outer').remove();

          // Build deformed contour in absolute coordinates, then translate to group-relative
          const contourPoints = buildOuterContour(0, 0, outerR, 360);
          const pathD = `M${contourPoints
            .map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`)
            .join('L')}Z`;

          shapeGroupAll
            .selectAll('path.outer-contour')
            .data([pathD])
            .join('path')
            .classed('outer-contour', true)
            .attr('d', d => d)
            .attr('fill', 'none')
            .attr('stroke', 'white')
            .attr('stroke-width', 1);
        }
        else {
          // Remove path if it exists
          shapeGroupAll.selectAll('path.outer-contour').remove();

          shapeGroupAll
            .selectAll('circle.outer')
            .data([outerR])
            .join('circle')
            .classed('outer', true)
            .attr('r', d => d)
            .attr('fill', 'none')
            .attr('stroke', 'white')
            .attr('stroke-width', 1);
        }

        // Insertion point white dot — follows deformed contour if morphology is active
        const insertionTheta = 0; // theta=0 in data space = the insertion point
        const insertionLocalR = getOuterRAtAngle(insertionTheta, outerR);
        const startRad = ((props.insertionPoint - 90) * Math.PI) / 180;
        const markerX = insertionLocalR * Math.cos(startRad);
        const markerY = insertionLocalR * Math.sin(startRad);

        shapeGroupAll
          .selectAll('circle.marker')
          .data([{ x: markerX, y: markerY }])
          .join('circle')
          .classed('marker', true)
          .attr('cx', d => d.x)
          .attr('cy', d => d.y)
          .attr('r', 4)
          .attr('fill', 'white');

        // --- Territory separators (LAD / RCA / LCx) ---
        if (props.showTerritories) {
          const territoryLayout = getTerritoryLayout();

          const territoryLabelData = territoryLayout.labels.map((territory) => {
            const localOuterR = getOuterRAtAngle(territory.theta, outerR);
            const screenAngle = dataThetaToScreenAngle(territory.theta);
            const labelR = localOuterR + 18;
            return {
              name: territory.name,
              x: labelR * Math.cos(screenAngle),
              y: labelR * Math.sin(screenAngle),
            };
          });

          shapeGroupAll
            .selectAll('text.territory-label')
            .data(territoryLabelData)
            .join('text')
            .classed('territory-label', true)
            .attr('x', d => d.x)
            .attr('y', d => d.y)
            .attr('fill', 'white')
            .attr('font-size', '11px')
            .attr('font-family', 'sans-serif')
            .attr('font-weight', 'bold')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .text(d => d.name);
        }
        else {
          shapeGroupAll.selectAll('line.territory-separator').remove();
          shapeGroupAll.selectAll('text.territory-label').remove();
        }
      }

      // --- Colorbar ---
      const legendHeight = Math.min(outerR * 2, c.height - 20);
      const legendY = cy - legendHeight / 2;
      const hasRoomOnRight
        = props.orientation === Orientation.Lax2CV && laxBounds
          ? legendX >= laxBounds.maxX
          : legendX >= cx + outerR;
      const legendLabelX = hasRoomOnRight
        ? legendX + legendWidth + 6
        : legendX - 6;
      const legendLabelAnchor = hasRoomOnRight ? 'start' : 'end';
      let defs = svg.select('defs');
      if (defs.empty()) defs = svg.append('defs');

      let gradient = defs.select(`#${gradientId}`);
      if (gradient.empty()) {
        gradient = defs
          .append('linearGradient')
          .attr('id', gradientId)
          .attr('gradientUnits', 'userSpaceOnUse');
      }
      gradient
        .attr('x1', 0)
        .attr('y1', legendY + legendHeight)
        .attr('x2', 0)
        .attr('y2', legendY)
        .selectAll('stop')
        .data(Array.from({ length: 256 }, (_, i) => i / 255))
        .join('stop')
        .attr('offset', d => `${d * 100}%`)
        .attr('stop-color', (d) => {
          if (props.colormapMode === 'z_score') {
            // Map [0, 1] to [-clipN, clipN]
            const zVal = -props.clipN + d * 2 * props.clipN;
            return getZScoreColor(zVal);
          }
          return colormaps[props.colormap](d);
        });

      const legendGroup = svg
        .selectAll('g.colorbar')
        .data([null])
        .join('g')
        .classed('colorbar', true);

      legendGroup
        .selectAll('rect.colorbar')
        .data([null])
        .join('rect')
        .classed('colorbar', true)
        .attr('x', legendX)
        .attr('y', legendY)
        .attr('width', legendWidth)
        .attr('height', legendHeight)
        .attr('fill', `url(#${gradientId})`)
        .attr('stroke', 'white')
        .attr('stroke-width', 1);

      const valueScale = scaleLinear()
        .domain([maxValue.value, minValue.value])
        .range([legendY, legendY + legendHeight]);

      legendGroup
        .selectAll('text.legend-label')
        .data([minValue.value, maxValue.value])
        .join('text')
        .classed('legend-label', true)
        .attr('x', legendLabelX)
        .attr('y', d =>
          d === maxValue.value ? valueScale(d) + 10 : valueScale(d) - 2)
        .attr('fill', 'white')
        .attr('font-size', '12px')
        .attr('font-family', 'sans-serif')
        .attr('text-anchor', legendLabelAnchor)
        .text((d) => {
          if (props.colormapMode === 'z_score') {
            return d.toFixed(2);
          }
          return d.toFixed(1);
        });

      // --- Title + Unit ---
      // const textGroup = svg
      //   .selectAll('g.text-field')
      //   .data([null])
      //   .join('g')
      //   .classed('text-field', true);

      // textGroup
      //   .selectAll('text.title')
      //   .data([`${props.title} (${props.unit})`])
      //   .join('text')
      //   .classed('title', true)
      //   .attr('x', c.width - 20)
      //   .attr('y', c.height - 20)
      //   .attr('text-anchor', 'end')
      //   .attr('fill', 'white')
      //   .attr('font-size', '14px')
      //   .attr('font-family', 'sans-serif')
      //   .attr('font-weight', 'bold')
      //   .text((d) => d);
    };

    const onMouseMove = (event) => {
      const c = canvas.value;
      const data = getDataArray();
      if (!c || !data || !data.length) return;

      const rect = c.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const { cx, cy, innerR, outerR } = geometry.value;
      const sectorInfo = getSectorInfo(x, y, cx, cy, innerR, outerR);

      if (!sectorInfo || sectorInfo.value == null) {
        tooltip.value.visible = false;
        return;
      }

      const rawValStr = props.unit
        ? `${sectorInfo.value.toFixed(2)} ${props.unit}`
        : `${sectorInfo.value.toFixed(2)}`;

      let label = `Sector ${sectorInfo.index + 1}: ${rawValStr}`;
      if (props.colormapMode === 'z_score') {
        const zScore = calculateZScore(sectorInfo.value);
        const zScoreStr = zScore != null ? zScore.toFixed(2) : 'N/A';
        label = `Sector ${sectorInfo.index + 1}: Raw: ${rawValStr}, Z-Score: ${zScoreStr}`;
      }

      tooltip.value = {
        visible: true,
        x: x + 12,
        y: y + 12,
        label,
      };
    };

    const onMouseLeave = () => {
      tooltip.value.visible = false;
    };

    // Throttle mechanism to prevent excessive rendering (max once per 100ms)
    let lastRenderTime = 0;
    const throttledDraw = () => {
      const now = Date.now();
      if (now - lastRenderTime >= 100) {
        lastRenderTime = now;
        draw();
      }
      else if (!throttledDraw.timeoutId) {
        throttledDraw.timeoutId = setTimeout(() => {
          throttledDraw.timeoutId = null;
          draw();
        }, 100 - (now - lastRenderTime));
      }
    };
    throttledDraw.timeoutId = null;

    const handleResize = () => {
      const parent = canvas.value?.parentElement;
      if (!parent) return;

      const nextSize = `${parent.clientWidth}x${parent.clientHeight}`;
      if (nextSize === lastObservedSize) return;

      lastObservedSize = nextSize;
      throttledDraw();
    };

    onMounted(() => {
      const parent = canvas.value?.parentElement;
      if (!parent) return;

      lastObservedSize = `${parent.clientWidth}x${parent.clientHeight}`;
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(parent);
      throttledDraw();
    });

    onBeforeUnmount(() => {
      if (throttledDraw.timeoutId) {
        clearTimeout(throttledDraw.timeoutId);
      }
      resizeObserver?.disconnect();
      const parent = canvas.value?.parentElement;
      if (parent) {
        select(parent).select('svg').remove();
      }
    });
    watch(() => viewerIndx.value.sliceIdx, throttledDraw);
    watch(() => viewerIndx.value.seriesIdx, throttledDraw);

    watch(
      () => ({
        colormap: props.colormap,
        insertionPoint: props.insertionPoint,
        interpolationMode: props.interpolationMode,
        pixelSize: props.pixelSize,
        title: props.title,
        unit: props.unit,
        trueMorphology: props.trueMorphology,
        morphology: props.morphology,
        morphologyScale: props.morphologyScale,
        colormapMode: props.colormapMode,
        muHealthy: props.muHealthy,
        sigmaHealthy: props.sigmaHealthy,
        clipN: props.clipN,
        zDiff: props.zDiff,
        badColor: props.badColor,
        goodColor: props.goodColor,
        showTerritories: props.showTerritories,
        orientation: props.orientation,
        laxRotationDeg: props.laxRotationDeg,
      }),
      () => {
        tooltip.value.visible = false;
        throttledDraw();
      },
      { deep: true },
    );

    return { canvas, tooltip, onMouseMove, onMouseLeave };
  },
};
</script>

<template>
  <h5 v-if="clinicalLabel" class="text-center mb-4">
    {{ clinicalLabel }}
  </h5>
  <div class="polar-map-container">
    <canvas
      ref="canvas"
      class="polar-map-canvas"
      @mousemove="onMouseMove"
      @mouseleave="onMouseLeave"
    />
    <div
      v-if="tooltip.visible"
      class="polar-map-tooltip"
      :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }"
    >
      {{ tooltip.label }}
    </div>
  </div>
</template>

<style scoped>
.polar-map-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.polar-map-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.polar-map-tooltip {
  position: absolute;
  transform: translate(0, 0);
  pointer-events: none;
  z-index: 20;
  background: rgba(15, 15, 15, 0.9);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-family: sans-serif;
  white-space: nowrap;
}
</style>
